import { ToolkitProperty, ToolkitPropertyEnum, expected } from '@aloft/toolkit-shared';

import { useEffect } from 'react';

import { getPropertyPinIcon } from '../../../../components/Tkv2Map/utils/getPropertyPinIcon.ts';
import MapBox from '../../../../components/map/MapBox.ts';
import { MAP_SOURCES } from '../../../../components/map/constants.ts';
import { PairedSaleIDs } from '../../types.ts';
import { PinsSourceFeature } from './types.ts';

const mapPairToPinSourceFeature = (
  property: ToolkitProperty,
  pair: PairedSaleIDs,
  isSelected: boolean,
): PinsSourceFeature | undefined => {
  const { latitude, longitude } = property;
  if (latitude === undefined || longitude === undefined) return undefined;

  return {
    type: 'Feature',
    geometry: {
      type: 'Point',
      coordinates: [longitude, latitude],
    },
    properties: {
      icon: getPropertyPinIcon(
        expected(property.listingStatus) as ToolkitPropertyEnum<'ListingStatusType'>,
        isSelected,
      ),
      pair,
    },
  };
};

// Converts ToolkitProperties into a GeoJSON FeatureCollection and sets it as the data for the pins map source
export const useSetPinsSource = ({
  mapRef,
  pairs,
  selectedPairs,
  properties,
  canSetSource,
}: {
  mapRef: React.MutableRefObject<MapBox | null>;
  pairs: PairedSaleIDs[];
  selectedPairs: PairedSaleIDs[];
  properties: Record<string, ToolkitProperty>;
  canSetSource: boolean;
}) => {
  useEffect(() => {
    if (!mapRef.current || !canSetSource) return;

    const map = mapRef.current.getMap();
    const source = map.getSource(MAP_SOURCES.PINS);

    if (source?.type !== 'geojson') return;

    const selectedPairsSet = new Set(selectedPairs.map((pair) => pair.p1 + pair.p2));

    const features = pairs.flatMap((pair) => {
      const p1 = properties[pair.p1];
      const p2 = properties[pair.p2];
      if (!p1 || !p2) return [];

      const isSelected = selectedPairsSet.has(pair.p1 + pair.p2);
      const p1pin = mapPairToPinSourceFeature(p1, pair, isSelected);
      const p2pin = mapPairToPinSourceFeature(p2, pair, isSelected);
      if (!p1pin || !p2pin) return [];

      return [p1pin, p2pin];
    });

    source.setData({
      type: 'FeatureCollection',
      features,
    });
  }, [canSetSource, pairs, properties, mapRef, selectedPairs]);
};
